/**
 * Membership 5787 — keeping an emailed deep link alive across sign-in.
 *
 * Every Give Butter activation email links to `/my-account?section=seats`,
 * but MyAccount's auth effect bounces a logged-out visitor to `/` — which
 * used to discard the destination, leaving the member on the homepage with
 * no route back to their seats. The page stashes the intended path on its
 * way out and the header consumes it once sign-in completes.
 *
 * Kept as a pure module (like guestSeatValidation.js and
 * highHolidaySeats.js) so the path-safety rules below are unit-testable
 * without standing up render tooling for the header and the account page.
 */
const STORAGE_KEY = 'kbt:returnTo';

// Long enough for any real in-app path, short enough that a junk value
// can't be parked in storage.
const MAX_LENGTH = 2048;

/**
 * True only for a path this app can navigate to itself.
 *
 * The stored value is attacker-reachable in principle — anything that can
 * write sessionStorage for this origin can set it — so it is re-validated
 * on the way out, not just on the way in. Rejecting everything that is not
 * a rooted, single-slash, control-character-free path means this cannot
 * become an open redirect:
 *
 *   - `'//evil.com'.startsWith('/')` is `true`, and a protocol-relative
 *     URL sends the browser off-site, so a bare `startsWith('/')` check is
 *     not enough — the second character must not be another slash.
 *   - Browsers also read a backslash as a slash when resolving a URL, so
 *     `/\evil.com` and `/\/evil.com` are off-site too. Backslashes have no
 *     place in a real path here, so they are rejected outright.
 *   - Control characters and spaces are stripped or re-encoded
 *     inconsistently while a URL is resolved, so a value containing them
 *     cannot be reasoned about. `window.location.pathname`/`search` are
 *     already percent-encoded and never contain them.
 *
 * `https://evil.com`, `javascript:alert(1)`, `''`, `null` and non-strings
 * all fail the rooted-path test above.
 */
export const isSafeReturnPath = (value) => {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > MAX_LENGTH) return false;
  if (value[0] !== '/') return false;
  if (value[1] === '/' || value[1] === '\\') return false;
  if (value.includes('\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0020\u007f]/.test(value)) return false;
  return true;
};

/**
 * Remember where the member was heading. Returns whether it was stored, so
 * a caller can tell "refused" from "written" — though every caller here
 * redirects either way.
 *
 * A storage failure (private mode, storage disabled, quota) is swallowed:
 * losing the destination degrades to the old behaviour of landing on the
 * homepage, which is far better than an exception blocking the redirect and
 * stranding the member on a page that will never load.
 */
export const stashReturnTo = (path) => {
  if (!isSafeReturnPath(path)) return false;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, path);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Read the stashed path and clear it, returning it only if it is still a
 * safe in-app path.
 *
 * Clearing is the point of "consume": without it a member who signs in
 * once for a deep link would be bounced to that same page on every later
 * sign-in in the tab. The remove runs before validation so even a value
 * that fails the check is cleared rather than left to be re-read.
 *
 * If storage throws we report "nowhere to go" and the caller stays put.
 * That includes the case where the read worked but the clear did not —
 * returning the path there would risk a value that can never be cleared
 * redirecting every future sign-in in the tab.
 */
export const consumeReturnTo = () => {
  let stored = null;
  try {
    stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
  return isSafeReturnPath(stored) ? stored : null;
};

/**
 * Look at the stashed path without clearing it. Used to open the sign-in
 * modal when a logged-out visitor was bounced off /my-account — consume
 * still happens only after they actually sign in.
 */
export const peekReturnTo = () => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return isSafeReturnPath(stored) ? stored : null;
  } catch (_) {
    return null;
  }
};

/**
 * Emailed "Login To My Account" lands on /signin?next=/my-account?section=seats
 * (or ?signin=1 on any page). `next` is re-checked with isSafeReturnPath
 * so this cannot become an open redirect.
 */
export const parseSignInDeepLink = (search = '', pathname = '') => {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(search);
  const flagged = path === '/signin' || params.get('signin') === '1';
  if (!flagged) return { open: false, next: null };
  const raw = params.get('next') || '';
  const next = isSafeReturnPath(raw) ? raw : '/my-account';
  return { open: true, next };
};

export const RETURN_TO_STORAGE_KEY = STORAGE_KEY;
